import { z } from 'zod';

export const WHITE_LABEL_FEATURE_FIELDS = [
  { key: 'agent_management', label: 'Agent Management' },
  { key: 'employee_management', label: 'Employee Management' },
  { key: 'commission_module', label: 'Commission Module' },
  { key: 'report_export', label: 'Report Export' },
  { key: 'bank_settings', label: 'Bank/NBFC Settings' },
  { key: 'custom_branding', label: 'Custom Branding' },
  { key: 'audit_logs', label: 'Audit Logs' },
];

export const WHITE_LABEL_COMMISSION_PLANS = [
  { value: 'premium', label: 'Premium (30% WL / 70% Admin)' },
  { value: 'standard', label: 'Standard (20% WL / 80% Admin)' },
  { value: 'basic', label: 'Basic (10% WL / 90% Admin)' },
];

export const WHITE_LABEL_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

export const WHITE_LABEL_DEFAULT_FEATURES = WHITE_LABEL_FEATURE_FIELDS.reduce(
  (acc, item) => ({ ...acc, [item.key]: true }),
  {}
);

export const WHITE_LABEL_DEFAULT_FORM_VALUES = {
  brandName: '',
  ownerName: '',
  adminName: '',
  adminEmail: '',
  adminPass: '',
  adminPassword: '',
  phone: '',
  domain: '',
  primaryColor: '#6366f1',
  secondaryColor: '#8b5cf6',
  commissionPlan: 'standard',
  pricingPlanId: '',
  status: 'active',
  featurePermissions: WHITE_LABEL_DEFAULT_FEATURES,
};

const NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 '&.,()-]*$/;
const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;
const PASSWORD_LOWERCASE_PATTERN = /[a-z]/;
const PASSWORD_UPPERCASE_PATTERN = /[A-Z]/;
const PASSWORD_NUMBER_PATTERN = /\d/;
const PASSWORD_SPECIAL_PATTERN = /[^A-Za-z0-9]/;
const PHONE_PATTERN = /^\+?[1-9]\d{9,14}$/;
const DOMAIN_PATTERN = /^(?=.{1,255}$)(?!-)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

const API_FIELD_MAP = {
  brand_name: 'brandName',
  owner_name: 'ownerName',
  admin_name: 'adminName',
  admin_email: 'adminEmail',
  admin_password: 'adminPass',
  phone: 'phone',
  custom_domain: 'domain',
  primary_color: 'primaryColor',
  secondary_color: 'secondaryColor',
  commission_plan: 'commissionPlan',
  pricing_plan_id: 'pricingPlanId',
  feature_permissions: 'featurePermissions',
  status: 'status',
};

export function normalizeWhitespace(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

export function sanitizeEmail(value) {
  return normalizeWhitespace(value).toLowerCase();
}

export function sanitizePhone(value) {
  const normalized = normalizeWhitespace(value);
  if (!normalized) return '';
  let sanitized = normalized.replace(/[\s().-]/g, '');
  if (sanitized.startsWith('00')) {
    sanitized = `+${sanitized.slice(2)}`;
  }
  return sanitized;
}

export function sanitizeDomain(value) {
  let domain = normalizeWhitespace(value).toLowerCase();
  if (!domain) return '';
  domain = domain.replace(/^https?:\/\//, '');
  domain = domain.replace(/\/.*$/, '');
  domain = domain.replace(/[?#].*$/, '');
  domain = domain.replace(/\.$/, '');
  return domain;
}

export function sanitizeColor(value, fallback) {
  const sanitized = normalizeWhitespace(value).toLowerCase();
  return HEX_COLOR_PATTERN.test(sanitized) ? sanitized : fallback;
}

const nameSchema = z
  .string()
  .transform(normalizeWhitespace)
  .pipe(
    z
      .string()
      .min(2, 'Must be at least 2 characters.')
      .max(150, 'Must be 150 characters or fewer.')
      .refine((value) => NAME_PATTERN.test(value), 'Use letters, numbers, spaces, and standard punctuation only.')
  );

const emailSchema = z
  .string()
  .transform(sanitizeEmail)
  .pipe(z.string().min(1, 'Email is required.').email('Enter a valid email address.').max(254, 'Email is too long.'));

const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters.')
  .max(72, 'Password must be 72 characters or fewer.')
  .refine((value) => PASSWORD_LOWERCASE_PATTERN.test(value), 'Password must include at least one lowercase letter.')
  .refine((value) => PASSWORD_UPPERCASE_PATTERN.test(value), 'Password must include at least one uppercase letter.')
  .refine((value) => PASSWORD_NUMBER_PATTERN.test(value), 'Password must include at least one number.')
  .refine((value) => PASSWORD_SPECIAL_PATTERN.test(value), 'Password must include at least one special character.');

const optionalPasswordSchema = z
  .string()
  .default('')
  .transform((value) => value.trim())
  .refine(
    (value) => !value || value.length >= 8,
    'Password must be at least 8 characters.'
  )
  .refine(
    (value) => !value || value.length <= 72,
    'Password must be 72 characters or fewer.'
  )
  .refine(
    (value) => !value || PASSWORD_LOWERCASE_PATTERN.test(value),
    'Password must include at least one lowercase letter.'
  )
  .refine(
    (value) => !value || PASSWORD_UPPERCASE_PATTERN.test(value),
    'Password must include at least one uppercase letter.'
  )
  .refine(
    (value) => !value || PASSWORD_NUMBER_PATTERN.test(value),
    'Password must include at least one number.'
  )
  .refine(
    (value) => !value || PASSWORD_SPECIAL_PATTERN.test(value),
    'Password must include at least one special character.'
  );

const phoneSchema = z
  .string()
  .default('')
  .transform(sanitizePhone)
  .refine((value) => !value || PHONE_PATTERN.test(value), 'Enter a valid phone number with 10 to 15 digits.');

const domainSchema = z
  .string()
  .default('')
  .transform(sanitizeDomain)
  .refine((value) => !value || DOMAIN_PATTERN.test(value), 'Enter a valid domain like portal.example.com.')
  .refine(
    (value) => !value || !/^www\./.test(value),
    'Enter the domain without a protocol. Example: portal.example.com'
  );

const colorSchema = (fallback) =>
  z
    .string()
    .transform((value) => sanitizeColor(value, fallback))
    .pipe(z.string().regex(HEX_COLOR_PATTERN, 'Select a valid 6-digit hex color.'));

const featurePermissionsSchema = z
  .object(
    WHITE_LABEL_FEATURE_FIELDS.reduce(
      (acc, item) => ({ ...acc, [item.key]: z.boolean() }),
      {}
    )
  )
  .strict()
  .refine(
    (value) => Object.values(value).some(Boolean),
    'Enable at least one feature permission.'
  );

const baseWhiteLabelSchema = z.object({
  brandName: nameSchema,
  ownerName: nameSchema,
  adminName: nameSchema,
  adminEmail: emailSchema,
  phone: phoneSchema,
  domain: domainSchema,
  primaryColor: colorSchema('#6366f1'),
  secondaryColor: colorSchema('#8b5cf6'),
  commissionPlan: z.enum(WHITE_LABEL_COMMISSION_PLANS.map((item) => item.value), {
    errorMap: () => ({ message: 'Select a valid commission plan.' }),
  }),
  pricingPlanId: z.string().default('').transform((value) => normalizeWhitespace(value)),
  status: z.enum(WHITE_LABEL_STATUSES.map((item) => item.value), {
    errorMap: () => ({ message: 'Select a valid status.' }),
  }),
  featurePermissions: featurePermissionsSchema,
});

export const createWhiteLabelFormSchema = baseWhiteLabelSchema.extend({
  adminPass: passwordSchema,
});

export const editWhiteLabelFormSchema = baseWhiteLabelSchema.extend({
  adminPassword: optionalPasswordSchema,
});

export function buildWhiteLabelCreatePayload(values, pricingPlans = []) {
  const parsed = createWhiteLabelFormSchema.parse(values);
  validatePricingPlanSelection(parsed.pricingPlanId, pricingPlans);

  return {
    brand_name: parsed.brandName,
    owner_name: parsed.ownerName,
    admin_name: parsed.adminName,
    admin_email: parsed.adminEmail,
    admin_password: parsed.adminPass,
    phone: parsed.phone || null,
    custom_domain: parsed.domain || null,
    primary_color: parsed.primaryColor,
    secondary_color: parsed.secondaryColor,
    commission_plan: parsed.commissionPlan,
    pricing_plan_id: parsed.pricingPlanId || null,
    status: parsed.status,
    feature_permissions: parsed.featurePermissions,
  };
}

export function buildWhiteLabelUpdatePayload(values, pricingPlans = []) {
  const parsed = editWhiteLabelFormSchema.parse(values);
  validatePricingPlanSelection(parsed.pricingPlanId, pricingPlans);

  return {
    brand_name: parsed.brandName,
    owner_name: parsed.ownerName,
    admin_name: parsed.adminName,
    admin_email: parsed.adminEmail,
    admin_password: parsed.adminPassword || null,
    phone: parsed.phone || null,
    custom_domain: parsed.domain || null,
    primary_color: parsed.primaryColor,
    secondary_color: parsed.secondaryColor,
    commission_plan: parsed.commissionPlan,
    pricing_plan_id: parsed.pricingPlanId || null,
    status: parsed.status,
    feature_permissions: parsed.featurePermissions,
  };
}

export function mapWhiteLabelToFormValues(whiteLabel) {
  return {
    ...WHITE_LABEL_DEFAULT_FORM_VALUES,
    brandName: whiteLabel?.brandName || '',
    ownerName: whiteLabel?.ownerName || '',
    adminName: whiteLabel?.adminName || '',
    adminEmail: whiteLabel?.adminEmail || '',
    phone: whiteLabel?.phone || '',
    domain: whiteLabel?.domain || '',
    primaryColor: sanitizeColor(whiteLabel?.primaryColor || '', '#6366f1'),
    secondaryColor: sanitizeColor(whiteLabel?.secondaryColor || '', '#8b5cf6'),
    commissionPlan: whiteLabel?.commissionPlan || 'standard',
    pricingPlanId: whiteLabel?.pricingPlanId || '',
    status: whiteLabel?.status || 'active',
    featurePermissions: WHITE_LABEL_FEATURE_FIELDS.reduce(
      (acc, item) => ({
        ...acc,
        [item.key]: Boolean(whiteLabel?.featurePermissions?.[item.key]),
      }),
      {}
    ),
  };
}

export function validatePricingPlanSelection(pricingPlanId, pricingPlans = []) {
  if (!pricingPlanId) return;
  const exists = pricingPlans.some((plan) => String(plan?.id || '') === pricingPlanId);
  if (!exists) {
    const error = new Error('Select a valid pricing plan.');
    error.fieldErrors = {
      pricing_plan_id: 'Select a valid pricing plan.',
    };
    throw error;
  }
}

export function mapWhiteLabelApiErrors(error, mode = 'create') {
  const fieldErrors = {};
  const apiFieldErrors = error?.fieldErrors || {};

  Object.entries(apiFieldErrors).forEach(([key, message]) => {
    const formField = API_FIELD_MAP[key];
    if (!formField || !message) return;

    if (formField === 'adminPass' && mode !== 'create') {
      fieldErrors.adminPassword = String(message);
      return;
    }

    fieldErrors[formField] = String(message);
  });

  const message = String(error?.message || '');
  if (/brand name already exists/i.test(message)) {
    fieldErrors.brandName = 'This brand name is already in use.';
  }
  if (/custom domain already exists/i.test(message)) {
    fieldErrors.domain = 'This custom domain is already in use.';
  }
  if (/admin email already registered/i.test(message)) {
    fieldErrors.adminEmail = 'This admin email is already in use.';
  }
  if (/selected pricing plan is invalid/i.test(message)) {
    fieldErrors.pricingPlanId = 'Select a valid pricing plan.';
  }

  return fieldErrors;
}

export function getWhiteLabelErrorMessage(error, fallbackMessage) {
  const message = String(error?.message || '').trim();

  if (!message) return fallbackMessage;
  if (message === '[object Object]') return fallbackMessage;
  if (/brand name already exists/i.test(message)) return 'Brand name already exists.';
  if (/custom domain already exists/i.test(message)) return 'Custom domain already exists.';
  if (/admin email already registered/i.test(message)) return 'Admin email already registered.';
  if (/selected pricing plan is invalid/i.test(message)) return 'The selected pricing plan is invalid.';

  return message;
}
